// RevXcel — AnnualPlan.jsx  (Plan Builder + Setup Wizard)
// All settings fields save back to global settings via onSave prop.
import React, { useState, useMemo, useCallback } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { RefreshCw, Plus, Trash2, CheckCircle, ChevronRight, Users, Target, TrendingUp, BarChart2, Settings, AlertTriangle, Link, Wifi, WifiOff, Search, Database, Building2 } from "lucide-react";
import { Card, SH, fmtM, Inp, PROXY, resolveF } from "../shared/constants";
import { OwnerTypeahead } from "../shared/components";
import { fetchOwners } from "../shared/hubspotHelpers";
import { StageMapper, FieldMapper } from "./SettingsScreen";

// ─── helpers ────────────────────────────────────────────────────────────────
const fmtPct = (v, d = 0) => v == null ? "—" : `${(v * 100).toFixed(d)}%`;
const fmtNum = (v) => v == null ? "—" : Number(v).toLocaleString();
const parseDollar = (raw) => {
  const s = String(raw || "").replace(/[^0-9.KkMm]/g, "");
  if (!s) return 0;
  if (/[Mm]/.test(s)) return parseFloat(s) * 1_000_000;
  if (/[Kk]/.test(s)) return parseFloat(s) * 1_000;
  return parseFloat(s) || 0;
};

// Deep-set a dot-path in an object (immutably)
const deepSet = (obj, path, value) => {
  const keys = path.split(".");
  const clone = { ...obj };
  let cur = clone;
  for (let i = 0; i < keys.length - 1; i++) {
    cur[keys[i]] = { ...(cur[keys[i]] || {}) };
    cur = cur[keys[i]];
  }
  cur[keys[keys.length - 1]] = value;
  return clone;
};

// ─── local plan state (matrix + GTM draft) ──────────────────────────────────
const PLAN_KEY = "rx_annual_plan_v3";
const loadPlan = () => { try { return JSON.parse(localStorage.getItem(PLAN_KEY) || "{}"); } catch { return {}; } };
const savePlan = (d) => { try { localStorage.setItem(PLAN_KEY, JSON.stringify(d)); } catch {} };

// ─── Revenue matrix helpers ──────────────────────────────────────────────────
const MOTIONS = [
  { key: "new",     label: "New",        sub: "Net-new logos",                      color: "#CC1A1A" },
  { key: "upsell",  label: "Upsell",     sub: "Upgrades & add-ons",                 color: "#7C3AED" },
  { key: "xsell",   label: "Cross-sell", sub: "New products to existing customers", color: "#D97706" },
  { key: "renewal", label: "Renewal",    sub: "Renewals / retention",               color: "#16A34A" },
];
const MOTION_BM = [
  { key: "new",     bm: "40–60%", note: "Early-stage skews higher" },
  { key: "upsell",  bm: "10–20%", note: "Seat and tier expansions" },
  { key: "xsell",   bm: "5–15%",  note: "New products, existing customers" },
  { key: "renewal", bm: "20–30%", note: "GRR proxy — protect first" },
];
const getSegments  = (s) => (s?.segments?.list || []).map(sg => sg.name).filter(Boolean);
const getCell      = (plan, m, seg) => plan?.matrix?.[m]?.[seg] || "";
const setCell      = (plan, m, seg, v) => { const mx = { ...(plan.matrix || {}), [m]: { ...(plan.matrix?.[m] || {}), [seg]: v } }; return { ...plan, matrix: mx }; };
const motionTotal  = (plan, mk, segs) => segs.reduce((s, sg) => s + parseDollar(getCell(plan, mk, sg)), 0);
const segTotal     = (plan, seg) => MOTIONS.reduce((s, m) => s + parseDollar(getCell(plan, m.key, seg)), 0);
const grandTotal   = (plan, segs) => MOTIONS.reduce((s, m) => s + motionTotal(plan, m.key, segs), 0);

// ─── Segment colors ──────────────────────────────────────────────────────────
const SEG_COLORS = ["#CC1A1A", "#7C3AED", "#D97706", "#16A34A", "#0EA5E9", "#EC4899"];

// ─── Shared micro-components ─────────────────────────────────────────────────
const AlertBanner = ({ level = "red", children }) => {
  const cfg = {
    red:    { bg: "rgba(220,38,38,0.06)",  border: "rgba(220,38,38,0.20)",  dot: "#DC2626", text: "#7F1D1D" },
    yellow: { bg: "rgba(217,119,6,0.06)",  border: "rgba(217,119,6,0.20)",  dot: "#D97706", text: "#78350F" },
    green:  { bg: "rgba(22,163,74,0.06)",  border: "rgba(22,163,74,0.20)",  dot: "#16A34A", text: "#14532D" },
  }[level];
  return (
    <div style={{ marginBottom: 12, padding: "10px 14px", background: cfg.bg, border: `1px solid ${cfg.border}`, borderRadius: 8, display: "flex", alignItems: "flex-start", gap: 8 }}>
      <div style={{ width: 6, height: 6, borderRadius: "50%", background: cfg.dot, marginTop: 5, flexShrink: 0 }} />
      <div style={{ fontSize: 12, color: cfg.text, lineHeight: 1.5 }}>{children}</div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// STEP 1 — Revenue Target
// ─────────────────────────────────────────────────────────────────────────────
const RevenueTargetStep = ({ settings, upd }) => {
  const co  = settings.company  || {};
  const rev = settings.revenue  || {};
  const FY  = new Date().getFullYear();
  const MOS = ["jan","feb","mar","apr","may","jun","jul","aug","sep","oct","nov","dec"];
  const MOL = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

  return (
    <div>
      <SH title="Company & Fiscal Year" sub="Basic info that anchors all plan math" />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginBottom: 28 }}>
        {[
          { l: "Company Name", k: "company.name", val: co.name || "", ph: "Acme Inc." },
          { l: "CRM",          k: "company.crm",  val: co.crm  || "HubSpot", ph: "HubSpot" },
          { l: "Fiscal Year",  k: "company.fiscalYear", val: co.fiscalYear || String(FY), ph: String(FY) },
        ].map(f => (
          <div key={f.k}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#555", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 5 }}>{f.l}</div>
            <input value={f.val} onChange={e => upd(f.k, e.target.value)} placeholder={f.ph}
              style={{ width: "100%", padding: "9px 12px", background: "#F8F8F8", border: "1.5px solid rgba(204,26,26,0.14)", borderRadius: 8, fontSize: 13, fontWeight: 600, color: "#111", outline: "none", boxSizing: "border-box" }} />
          </div>
        ))}
      </div>

      <SH title="Annual Bookings Target" sub="Your global POR number — the single source of truth for all gap math" />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 28 }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#555", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 5 }}>Annual Revenue Target</div>
          <div style={{ display: "flex", alignItems: "center", background: "#F8F8F8", border: "1.5px solid rgba(204,26,26,0.20)", borderRadius: 8, overflow: "hidden" }}>
            <span style={{ padding: "9px 12px", fontSize: 13, color: "#888", background: "#F0F0F0", borderRight: "1px solid rgba(0,0,0,0.08)" }}>$</span>
            <input value={rev.target || ""} onChange={e => upd("revenue", { ...(settings.revenue || {}), target: e.target.value, configured: true })} placeholder="e.g. 10M or 10000000"
              style={{ flex: 1, padding: "9px 12px", border: "none", background: "transparent", fontSize: 14, fontWeight: 700, color: "#111", outline: "none", fontFamily: "'DM Mono',monospace" }} />
          </div>
          {rev.target && <div style={{ marginTop: 6, fontSize: 13, fontWeight: 700, color: "#CC1A1A", fontFamily: "'DM Mono',monospace" }}>= {fmtM(parseDollar(rev.target))}</div>}
        </div>
        <div style={{ padding: "16px 18px", background: "rgba(204,26,26,0.04)", border: "1px solid rgba(204,26,26,0.12)", borderRadius: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#CC1A1A", marginBottom: 6 }}>💡 How this flows downstream</div>
          <div style={{ fontSize: 11, color: "#555", lineHeight: 1.7 }}>
            This target powers <strong>every screen</strong> — POR Dashboard, Morning Home, Quota Capacity, GTM math, and gap calculations on this page.
          </div>
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#111111" }}>Monthly Targets <span style={{ fontSize: 11, fontWeight: 400, color: "#888" }}>(optional)</span></div>
          <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>Blank by default — click a spread button or type your own values</div>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {/* Spread evenly */}
          <button onClick={() => {
            const annual = parseDollar(rev.target || "");
            if (!annual) return;
            const even = Math.round(annual / 12);
            const mo = {};
            MOS.forEach(m => { mo[m] = String(even); });
            upd("revenue", { ...(settings.revenue || {}), monthlyTargets: mo });
          }} style={{ padding: "7px 14px", borderRadius: 7, border: "1.5px solid rgba(204,26,26,0.25)", background: "rgba(204,26,26,0.05)", color: "#CC1A1A", fontSize: 11, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>
            ÷ Spread Evenly
          </button>
          {/* Back-loaded */}
          <button onClick={() => {
            const annual = parseDollar(rev.target || "");
            if (!annual) return;
            // Weights: ramp up Jan–Jun, accelerate Jul–Dec
            const weights = [0.06, 0.07, 0.07, 0.08, 0.08, 0.08, 0.09, 0.09, 0.09, 0.10, 0.10, 0.09];
            const mo = {};
            MOS.forEach((m, i) => { mo[m] = String(Math.round(annual * weights[i])); });
            upd("revenue", { ...(settings.revenue || {}), monthlyTargets: mo });
          }} style={{ padding: "7px 14px", borderRadius: 7, border: "1.5px solid rgba(124,58,237,0.25)", background: "rgba(124,58,237,0.05)", color: "#7C3AED", fontSize: 11, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>
            📈 Back-Loaded
          </button>
          {/* Clear all */}
          {Object.values(rev.monthlyTargets || {}).some(v => v) && (
            <button onClick={() => upd("revenue", { ...(settings.revenue || {}), monthlyTargets: {} })}
              style={{ padding: "7px 14px", borderRadius: 7, border: "1px solid rgba(0,0,0,0.10)", background: "transparent", color: "#888", fontSize: 11, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}>
              ✕ Clear
            </button>
          )}
        </div>
      </div>

      {/* Monthly sum vs annual check */}
      {Object.values(rev.monthlyTargets || {}).some(v => v) && (() => {
        const annual = parseDollar(rev.target || "");
        const moSum  = MOS.reduce((s, m) => s + parseDollar(rev.monthlyTargets?.[m] || ""), 0);
        const diff   = moSum - annual;
        if (!annual || Math.abs(diff) < 1) return null;
        return (
          <div style={{ marginBottom: 12, padding: "8px 14px", background: Math.abs(diff / annual) < 0.01 ? "rgba(22,163,74,0.06)" : "rgba(217,119,6,0.06)", border: `1px solid ${Math.abs(diff / annual) < 0.01 ? "rgba(22,163,74,0.20)" : "rgba(217,119,6,0.20)"}`, borderRadius: 7, fontSize: 11, color: Math.abs(diff / annual) < 0.01 ? "#14532D" : "#78350F", display: "flex", justifyContent: "space-between" }}>
            <span>Monthly total: <strong style={{ fontFamily: "'DM Mono',monospace" }}>{fmtM(moSum)}</strong></span>
            <span>{diff > 0 ? "+" : ""}<strong style={{ fontFamily: "'DM Mono',monospace" }}>{fmtM(diff)}</strong> vs. annual target</span>
          </div>
        );
      })()}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(6,1fr)", gap: 8 }}>
        {MOS.map((mo, i) => {
          const val = rev.monthlyTargets?.[mo] || "";
          return (
            <div key={mo}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>{MOL[i]}</div>
              <input value={val} onChange={e => upd("revenue", { ...(settings.revenue || {}), monthlyTargets: { ...(rev.monthlyTargets || {}), [mo]: e.target.value } })}
                placeholder="—"
                style={{ width: "100%", padding: "7px 9px", background: val ? "#FFF" : "#F8F8F8", border: `1px solid ${val ? "rgba(204,26,26,0.20)" : "rgba(0,0,0,0.08)"}`, borderRadius: 7, fontSize: 12, fontWeight: val ? 700 : 400, color: "#111", outline: "none", boxSizing: "border-box", fontFamily: "'DM Mono',monospace" }} />
              {val && <div style={{ fontSize: 9, color: "#CC1A1A", marginTop: 2, fontFamily: "'DM Mono',monospace" }}>{fmtM(parseDollar(val))}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// STEP 2 — Revenue Matrix  (teams as rows, motions as columns, inline team mgmt)
// ─────────────────────────────────────────────────────────────────────────────
const RevenueMatrixStep = ({ settings, upd, plan, setPlan }) => {
  const annualTarget = parseDollar(settings?.revenue?.target || "");

  // Seed teams from unique rep team names if segments not yet defined
  const repTeams = [...new Set((settings.team?.reps || []).map(r => r.team).filter(Boolean))];
  const rawList  = settings.segments?.list || [];
  const teamList = rawList.length > 0 ? rawList
    : repTeams.map((name, i) => ({ name, goal: "", color: SEG_COLORS[i % SEG_COLORS.length] }));

  // ── Team management (inline) ──────────────────────────────────────────────
  const setTeams = (l) => upd("segments", { ...(settings.segments || {}), list: l, configured: true });

  // Auto-save seeded teams to settings on mount so the completion check passes
  React.useEffect(() => {
    if (rawList.length === 0 && repTeams.length > 0) {
      setTeams(repTeams.map((name, i) => ({ name, goal: "", color: SEG_COLORS[i % SEG_COLORS.length] })));
    }
  }, []);
  const addTeam  = (name = "") => setTeams([...teamList, { name, goal: "", color: SEG_COLORS[teamList.length % SEG_COLORS.length] }]);
  const delTeam  = (i) => setTeams(teamList.filter((_, j) => j !== i));
  const renTeam  = (i, v) => { const l = [...teamList]; l[i] = { ...l[i], name: v }; setTeams(l); };

  const teamNames = teamList.map(t => t.name).filter(Boolean);

  // ── Matrix data helpers ───────────────────────────────────────────────────
  // Matrix is keyed by [team][motion] — teams on rows, motions on columns
  const getVal = (team, motionKey) => plan?.matrix?.[team]?.[motionKey] || "";
  const setVal = (team, motionKey, val) => {
    const mx   = { ...(plan.matrix || {}) };
    mx[team]   = { ...(mx[team] || {}), [motionKey]: val };
    const next = { ...plan, matrix: mx };
    setPlan(next);
    savePlan(next);
  };

  // Row total = sum of all motions for a team
  const teamRowTotal = (team) => MOTIONS.reduce((s, m) => s + parseDollar(getVal(team, m.key)), 0);
  // Column total = sum of all teams for a motion
  const motionColTotal = (motionKey) => teamNames.reduce((s, t) => s + parseDollar(getVal(t, motionKey)), 0);
  // Grand total
  const total = teamNames.reduce((s, t) => s + teamRowTotal(t), 0);
  const gap   = total - annualTarget;
  const gapPct = annualTarget > 0 ? gap / annualTarget : 0;

  const PRESETS = [
    { label: "Enterprise / Mid-Market / SMB", teams: ["Enterprise", "Mid-Market", "SMB"] },
    { label: "Enterprise / Commercial",        teams: ["Enterprise", "Commercial"] },
    { label: "Named Account / Territory",      teams: ["Named Accounts", "Territory"] },
    { label: "Single Team",                    teams: ["All Teams"] },
  ];

  // Derived: team % of total
  const teamPct = (team) => total > 0 ? teamRowTotal(team) / total : 0;

  return (
    <div>
      {/* ── Header + gap ── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12, marginBottom: 18 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 800, color: "#111", marginBottom: 3 }}>Revenue Plan Matrix</div>
          <div style={{ fontSize: 11, color: "#888" }}>Teams (rows) × Sales Motions (columns) · $M, $K, or raw — zero is fine</div>
        </div>
        {annualTarget > 0 && total > 0 && (
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 9, color: "#888", textTransform: "uppercase", letterSpacing: "0.07em" }}>vs. POR Target</div>
            <div style={{ fontSize: 22, fontWeight: 800, fontFamily: "'DM Mono',monospace", color: Math.abs(gapPct) < 0.02 ? "#16A34A" : gap >= 0 ? "#D97706" : "#DC2626" }}>
              {gap >= 0 ? "+" : ""}{fmtM(gap)}
            </div>
          </div>
        )}
      </div>

      {/* ── Team setup (shown when no teams yet) ── */}
      {teamList.length === 0 && (
        <div style={{ marginBottom: 20, padding: "18px 20px", background: "rgba(204,26,26,0.03)", border: "1.5px dashed rgba(204,26,26,0.25)", borderRadius: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#CC1A1A", marginBottom: 10 }}>First, define your team segments (the rows)</div>
          <div style={{ fontSize: 11, color: "#555", marginBottom: 14 }}>These are your go-to-market teams — e.g. Enterprise, Mid-Market, SMB. Sales motions (New, Upsell, etc.) are always the columns.</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {PRESETS.map((p, i) => (
              <button key={i} onClick={() => setTeams(p.teams.map((n, j) => ({ name: n, goal: "", color: SEG_COLORS[j % SEG_COLORS.length] })))}
                style={{ padding: "9px 16px", borderRadius: 8, border: "1.5px solid rgba(204,26,26,0.20)", background: "#FFF", color: "#CC1A1A", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                {p.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Main matrix table ── */}
      <Card style={{ marginBottom: 14 }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 560 }}>

            {/* Column headers = motions */}
            <thead>
              <tr style={{ background: "#F8F8F8" }}>
                {/* Team column header */}
                <th style={{ padding: "10px 16px", fontSize: 9, fontWeight: 700, color: "#888", textAlign: "left", textTransform: "uppercase", letterSpacing: "0.07em", width: 160, borderBottom: "2px solid #E8E8E8" }}>
                  Team
                </th>
                {/* Motion columns */}
                {MOTIONS.map(m => (
                  <th key={m.key} style={{ padding: "10px 10px", borderBottom: "2px solid #E8E8E8" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "center" }}>
                      <div style={{ width: 8, height: 8, borderRadius: "50%", background: m.color, flexShrink: 0 }} />
                      <div style={{ textAlign: "left" }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: "#111" }}>{m.label}</div>
                        <div style={{ fontSize: 9, color: "#AAA" }}>{m.sub}</div>
                      </div>
                    </div>
                  </th>
                ))}
                {/* Row total */}
                <th style={{ padding: "10px 14px", fontSize: 9, fontWeight: 700, color: "#CC1A1A", textAlign: "right", textTransform: "uppercase", letterSpacing: "0.07em", borderLeft: "2px solid #E8E8E8", borderBottom: "2px solid #E8E8E8", width: 90 }}>Total</th>
                <th style={{ padding: "10px 8px", fontSize: 9, fontWeight: 700, color: "#888", textAlign: "right", textTransform: "uppercase", letterSpacing: "0.07em", borderBottom: "2px solid #E8E8E8", width: 52 }}>%</th>
                <th style={{ width: 28, borderBottom: "2px solid #E8E8E8" }} />
              </tr>
            </thead>

            <tbody>
              {teamList.map((team, ti) => {
                const rowTotal = team.name ? teamRowTotal(team.name) : 0;
                const rowPct   = total > 0 ? rowTotal / total : 0;
                return (
                  <tr key={ti} style={{ borderBottom: "1px solid rgba(0,0,0,0.05)" }}>
                    {/* Team name cell */}
                    <td style={{ padding: "10px 12px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={{ width: 4, height: 32, borderRadius: 99, background: team.color || "#CC1A1A", flexShrink: 0 }} />
                        <input value={team.name} onChange={e => renTeam(ti, e.target.value)}
                          style={{ flex: 1, padding: "6px 8px", border: "1px solid rgba(0,0,0,0.10)", borderRadius: 6, fontSize: 12, fontWeight: 700, color: "#111", outline: "none", background: "#F8F8F8", minWidth: 0 }} />
                      </div>
                    </td>

                    {/* Motion input cells */}
                    {MOTIONS.map(m => {
                      const raw    = team.name ? getVal(team.name, m.key) : "";
                      const parsed = parseDollar(raw);
                      return (
                        <td key={m.key} style={{ padding: "8px 6px", verticalAlign: "top" }}>
                          <input value={raw} onChange={e => team.name && setVal(team.name, m.key, e.target.value)}
                            placeholder={team.name ? "0" : "—"} disabled={!team.name}
                            style={{ width: "100%", padding: "7px 8px", border: `1.5px solid ${parsed ? m.color + "55" : "rgba(0,0,0,0.08)"}`, borderRadius: 7, background: !team.name ? "#F4F4F4" : parsed ? "#FFF" : "#F8F8F8", fontSize: 12, fontWeight: parsed ? 700 : 400, fontFamily: "'DM Mono',monospace", color: team.name ? "#111" : "#CCC", outline: "none", textAlign: "right", boxSizing: "border-box" }} />
                          {parsed > 0 && <div style={{ fontSize: 9, color: m.color, textAlign: "right", marginTop: 2, fontFamily: "'DM Mono',monospace", fontWeight: 700 }}>{fmtM(parsed)}</div>}
                        </td>
                      );
                    })}

                    {/* Row total */}
                    <td style={{ padding: "10px 14px", textAlign: "right", borderLeft: "2px solid #E8E8E8", verticalAlign: "middle" }}>
                      <div style={{ fontSize: 13, fontWeight: 800, color: rowTotal > 0 ? "#111" : "#CCC", fontFamily: "'DM Mono',monospace" }}>{rowTotal > 0 ? fmtM(rowTotal) : "—"}</div>
                    </td>
                    <td style={{ padding: "10px 8px", textAlign: "right", verticalAlign: "middle" }}>
                      {rowTotal > 0 && (
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 700, color: team.color || "#CC1A1A", fontFamily: "'DM Mono',monospace" }}>{Math.round(rowPct * 100)}%</div>
                          <div style={{ marginTop: 3, width: 36, height: 3, background: "#F0F0F0", borderRadius: 99, marginLeft: "auto" }}>
                            <div style={{ width: `${Math.round(rowPct * 100)}%`, height: "100%", background: team.color || "#CC1A1A", borderRadius: 99 }} />
                          </div>
                        </div>
                      )}
                    </td>
                    <td style={{ padding: "8px 4px", textAlign: "center", verticalAlign: "middle" }}>
                      <button onClick={() => delTeam(ti)} style={{ background: "transparent", border: "none", cursor: "pointer", color: "#DDD", padding: 2, lineHeight: 1 }}
                        onMouseEnter={e => e.currentTarget.style.color = "#CC1A1A"}
                        onMouseLeave={e => e.currentTarget.style.color = "#DDD"}>
                        <Trash2 size={13} />
                      </button>
                    </td>
                  </tr>
                );
              })}

              {/* Empty-state row when teams exist but have no names */}
              {teamList.filter(t => t.name).length === 0 && teamList.length > 0 && (
                <tr><td colSpan={MOTIONS.length + 4} style={{ padding: "20px", textAlign: "center", color: "#AAA", fontSize: 12 }}>Type team names in the rows above to activate the matrix</td></tr>
              )}
            </tbody>

            {/* Column totals footer */}
            {teamNames.length > 0 && (
              <tfoot>
                <tr style={{ background: "#F0F0F0", borderTop: "2px solid #CC1A1A" }}>
                  <td style={{ padding: "10px 16px", fontSize: 10, fontWeight: 700, color: "#CC1A1A", textTransform: "uppercase", letterSpacing: "0.06em" }}>Total</td>
                  {MOTIONS.map(m => {
                    const ct   = motionColTotal(m.key);
                    const ctPct = total > 0 && ct > 0 ? Math.round(ct / total * 100) : 0;
                    return (
                      <td key={m.key} style={{ padding: "10px 6px", textAlign: "center" }}>
                        <div style={{ fontSize: 13, fontWeight: 800, color: ct > 0 ? m.color : "#CCC", fontFamily: "'DM Mono',monospace" }}>{ct > 0 ? fmtM(ct) : "—"}</div>
                        {ct > 0 && <div style={{ fontSize: 9, color: "#888", marginTop: 2 }}>{ctPct}%</div>}
                      </td>
                    );
                  })}
                  <td style={{ padding: "10px 14px", textAlign: "right", borderLeft: "2px solid #E8E8E8" }}>
                    <div style={{ fontSize: 15, fontWeight: 800, color: total > 0 ? "#CC1A1A" : "#CCC", fontFamily: "'DM Mono',monospace" }}>{total > 0 ? fmtM(total) : "—"}</div>
                    {annualTarget > 0 && total > 0 && <div style={{ fontSize: 9, color: "#888", marginTop: 2 }}>{Math.round(total / annualTarget * 100)}% of target</div>}
                  </td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {/* Add team row */}
        <div style={{ padding: "10px 16px", borderTop: "1px solid rgba(0,0,0,0.05)" }}>
          <button onClick={() => addTeam("")}
            style={{ display: "flex", alignItems: "center", gap: 6, padding: "7px 14px", borderRadius: 7, border: "1.5px dashed rgba(204,26,26,0.28)", background: "transparent", color: "#CC1A1A", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
            <Plus size={13} /> Add Team
          </button>
        </div>
      </Card>

      {/* Gap alert */}
      {total > 0 && annualTarget > 0 && (
        Math.abs(gapPct) < 0.02
          ? <AlertBanner level="green">Matrix total is within 2% of your POR target — aligned.</AlertBanner>
          : gap > 0
          ? <AlertBanner level="yellow">Matrix exceeds target by {fmtM(gap)} ({fmtPct(Math.abs(gapPct), 1)}). Intentional stretch?</AlertBanner>
          : <AlertBanner level="red">Matrix is {fmtM(Math.abs(gap))} ({fmtPct(Math.abs(gapPct), 1)}) short of POR target. Keep filling cells or adjust your target in Revenue Target.</AlertBanner>
      )}

      {/* ARR mix benchmarks */}
      <Card>
        <div style={{ padding: "14px 18px" }}>
          <SH title="Sales Motion Mix — Benchmarks" sub="What % of ARR typically comes from each motion in B2B SaaS" />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 8 }}>
            {MOTIONS.map((m, i) => {
              const bm   = MOTION_BM[i];
              const ct   = motionColTotal(m.key);
              const mPct = total > 0 && ct > 0 ? Math.round(ct / total * 100) : null;
              return (
                <div key={m.key} style={{ background: "#F8F8F8", borderRadius: 8, padding: "12px 14px", borderTop: `3px solid ${m.color}` }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: m.color }} />
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#111" }}>{m.label}</div>
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: m.color, fontFamily: "'DM Mono',monospace" }}>{bm.bm}</div>
                  <div style={{ fontSize: 10, color: "#AAA", margin: "3px 0 5px", lineHeight: 1.4 }}>{bm.note}</div>
                  {mPct !== null && (
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#555" }}>
                      Your plan: <span style={{ color: m.color }}>{mPct}%</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </Card>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// STEP 4 — Quota & Team
// ─────────────────────────────────────────────────────────────────────────────
const QuotaTeamStep = ({ settings, upd, hsData }) => {
  const reps = settings.team?.reps || [];
  const totalQuota = reps.reduce((s, r) => s + parseDollar(r.quota || ""), 0);

  // Load owners exactly like SettingsScreen does
  const [hsOwners, setHsOwners] = React.useState(hsData?.owners || []);
  const [ownersLoading, setOwnersLoading] = React.useState(false);
  const [ownersError, setOwnersError] = React.useState("");

  React.useEffect(() => {
    if (hsOwners.length) return; // already loaded
    const token = settings.hubspot?.token;
    if (!token) return;
    setOwnersLoading(true);
    fetchOwners(token)
      .then(o => { setHsOwners(o); if (!o.length) setOwnersError("0 owners returned — check crm.objects.owners.read scope."); })
      .catch(() => setOwnersError("Failed to load owners."))
      .finally(() => setOwnersLoading(false));
  }, [settings.hubspot?.token]);

  const setReps = (r) => upd("team", { ...(settings.team || {}), reps: r, configured: true });
  const addRep  = () => setReps([...reps, { name: "", team: "", quota: "", role: "AE", startDate: "", hubspotOwnerId: "" }]);
  const del     = (i) => setReps(reps.filter((_, j) => j !== i));
  const set     = (i, f, v) => { const r = [...reps]; r[i] = { ...r[i], [f]: v }; setReps(r); };



  return (
    <div>
      <SH title="Quota & Team" sub="Add your reps, assign each to a team, and set their quota — this feeds the Revenue Matrix and capacity math" />



      {/* Rep table — no overflow clip so typeahead dropdown can spill freely */}
        {/* Column headers */}
        <div style={{ display: "grid", gridTemplateColumns: "22% 16% 9% 14% 14% 1fr 28px", gap: 10, marginBottom: 6, padding: "0 4px" }}>
          {["Display Name", "Team", "Role", "Quota ($)", "Start Date", "HubSpot Owner", ""].map((h, idx) => (
            <div key={idx} style={{ fontSize: 10, fontWeight: 700, color: "#CC1A1A", textTransform: "uppercase", letterSpacing: "0.07em", paddingBottom: 6, borderBottom: "1px solid rgba(204,26,26,0.10)", whiteSpace: "nowrap" }}>{h}</div>
          ))}
        </div>

        {reps.length === 0 ? (
          <div style={{ padding: "40px 0", textAlign: "center", color: "#888" }}>
            <Users size={32} style={{ opacity: 0.3, display: "block", margin: "0 auto 10px" }} />
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>No reps added yet</div>
            <div style={{ fontSize: 11, marginBottom: 16 }}>Add reps to calculate total capacity and coverage ratio</div>
            <button onClick={addRep} style={{ padding: "10px 20px", background: "linear-gradient(135deg,#CC1A1A,#991B1B)", border: "none", borderRadius: 8, color: "#FFF", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>+ Add First Rep</button>
          </div>
        ) : (
          <div>
            {reps.map((rep, i) => {
              const teamColor = rep.team ? SEG_COLORS[[...new Set(reps.map(r=>r.team).filter(Boolean))].indexOf(rep.team) % SEG_COLORS.length] : "#CCC";
              return (
                <div key={i} style={{ display: "grid", gridTemplateColumns: "22% 16% 9% 14% 14% 1fr 28px", gap: 10, alignItems: "center", marginBottom: 6, position: "relative" }}>

                  {/* Name */}
                  <input value={rep.name || ""} onChange={e => set(i, "name", e.target.value)} placeholder="Auto-filled from HubSpot Owner"
                    style={{ padding: "8px 10px", border: "1px solid rgba(0,0,0,0.10)", borderRadius: 7, fontSize: 12, fontWeight: 600, color: "#111", outline: "none", background: "#F8F8F8", width: "100%", boxSizing: "border-box" }} />

                  {/* Team — free-form text, Revenue Matrix will pick these up as rows */}
                  <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                    {rep.team && <div style={{ width: 8, height: 8, borderRadius: "50%", background: teamColor, flexShrink: 0 }} />}
                    <input value={rep.team || ""} onChange={e => set(i, "team", e.target.value)} placeholder="e.g. Enterprise"
                      style={{ flex: 1, padding: "8px 10px", border: "1px solid rgba(0,0,0,0.10)", borderRadius: 7, fontSize: 12, color: "#111", outline: "none", background: "#F8F8F8", width: "100%", boxSizing: "border-box" }} />
                  </div>

                  {/* Role */}
                  <select value={rep.role || "AE"} onChange={e => set(i, "role", e.target.value)}
                    style={{ padding: "8px 8px", border: "1px solid rgba(0,0,0,0.10)", borderRadius: 7, fontSize: 12, color: "#111", outline: "none", background: "#F8F8F8", width: "100%" }}>
                    {["AE","BDR","SDR","CSM","AM","SE","Manager","Director"].map(r => <option key={r} value={r}>{r}</option>)}
                  </select>

                  {/* Quota */}
                  <div>
                    <input value={rep.quota || ""} onChange={e => set(i, "quota", e.target.value)} placeholder="1500000"
                      style={{ padding: "8px 10px", border: "1px solid rgba(0,0,0,0.10)", borderRadius: 7, fontSize: 12, fontWeight: 700, fontFamily: "'DM Mono',monospace", color: "#111", outline: "none", background: "#F8F8F8", width: "100%", boxSizing: "border-box" }} />
                    {rep.quota && <div style={{ fontSize: 9, color: "#CC1A1A", marginTop: 2, fontFamily: "'DM Mono',monospace" }}>{fmtM(parseDollar(rep.quota))}</div>}
                  </div>

                  {/* Start date — auto-filled from HubSpot createdAt, editable */}
                  <input value={rep.startDate || ""} onChange={e => set(i, "startDate", e.target.value)} type="date"
                    style={{ padding: "8px 8px", border: "1px solid rgba(0,0,0,0.10)", borderRadius: 7, fontSize: 12, color: "#111", outline: "none", background: "#F8F8F8", width: "100%", boxSizing: "border-box" }} />

                  {/* HubSpot Owner — negative margin cancels the built-in marginBottom:14 so it aligns in the grid */}
                  <div style={{ marginBottom: -14 }}>
                    <OwnerTypeahead
                      owners={hsOwners}
                      loading={ownersLoading}
                      value={rep.hubspotOwnerId || ""}
                      hasToken={!!settings.hubspot?.token}
                      onChange={v => {
                        const r = [...reps];
                        const autoDate = v.createdAt ? v.createdAt.slice(0, 10) : "";
                        r[i] = {
                          ...r[i],
                          hubspotOwnerId: v.id || "",
                          name: v.name || r[i].name || "",   // always auto-fill from HubSpot, user can edit after
                          startDate: r[i].startDate || autoDate,
                        };
                        setReps(r);
                      }}
                    />
                  </div>

                  {/* Delete */}
                  <button onClick={() => del(i)}
                    style={{ background: "transparent", border: "none", cursor: "pointer", color: "#DDD", padding: "8px 4px", lineHeight: 1 }}
                    onMouseEnter={e => e.currentTarget.style.color = "#CC1A1A"}
                    onMouseLeave={e => e.currentTarget.style.color = "#DDD"}>
                    <Trash2 size={13} />
                  </button>
                </div>
              );
            })}
          </div>
        )}

      {/* Add Rep + linked counter */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <button onClick={addRep} style={{ display: "flex", alignItems: "center", gap: 6, padding: "9px 16px", borderRadius: 8, border: "1.5px dashed rgba(204,26,26,0.30)", background: "transparent", color: "#CC1A1A", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
          <Plus size={13} /> Add Rep
        </button>
        {hsOwners.length > 0 && (
          <div style={{ fontSize: 11, color: "#888" }}>{reps.filter(r => r.hubspotOwnerId).length}/{reps.length} linked to HubSpot</div>
        )}
      </div>

      {/* Simple total quota summary */}
      {totalQuota > 0 && (
        <div style={{ marginTop: 12, padding: "10px 14px", background: "#F8F8F8", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: "#555" }}>{reps.filter(r => r.name).length} reps · Total quota</span>
          <span style={{ fontSize: 14, fontWeight: 800, color: "#CC1A1A", fontFamily: "'DM Mono',monospace" }}>{fmtM(totalQuota)}</span>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// STEP 5 — Performance Targets (per team)
// ─────────────────────────────────────────────────────────────────────────────
const PERF_FIELDS = [
  { k: "winRate",       l: "Win Rate",              suf: "%",  ph: "25",    tip: "Closed-won ÷ (won + lost).",               bm: "25–35%" },
  { k: "avgDealSize",   l: "Avg Deal Size (ACV)",   pre: "$",  ph: "25000", tip: "Average value of a closed-won deal.",       bm: "Varies by segment" },
  { k: "salesCycle",    l: "Sales Cycle",           suf: "d",  ph: "60",    tip: "Median days from deal creation to close.", bm: "45–90 days" },
  { k: "coverageRatio", l: "Pipeline Coverage",     suf: "×",  ph: "3",     tip: "Required pipeline ÷ revenue target.",     bm: "3–4×" },
  { k: "rampDays",      l: "Ramp Time",             suf: "d",  ph: "90",    tip: "Days from hire to full quota attainment.", bm: "90–120 days" },
  { k: "mqlTarget",     l: "MQL Target / Month",    ph: "200",              tip: "Marketing qualified leads per month.",      bm: "" },
  { k: "sqlTarget",     l: "SQL Target / Month",    ph: "60",               tip: "Sales qualified leads per month.",          bm: "" },
  { k: "bdrCount",      l: "BDR Headcount",         ph: "3",                tip: "Total BDR/SDR headcount.",                 bm: "" },
  { k: "aeCount",       l: "AE Headcount",          ph: "5",                tip: "Total AE headcount.",                      bm: "" },
  { k: "ltv",           l: "LTV (est.)",             pre: "$",  ph: "50000", tip: "Lifetime value estimate.",                 bm: "" },
  { k: "cac",           l: "CAC (est.)",             pre: "$",  ph: "5000",  tip: "Customer acquisition cost.",               bm: "" },
];

const PerfFieldCard = ({ f, value, onChange }) => (
  <div style={{ background: "#FFF", border: "1px solid rgba(0,0,0,0.07)", borderRadius: 10, padding: "14px 16px" }}>
    <div style={{ fontSize: 10, fontWeight: 700, color: "#555", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 6 }}>{f.l}</div>
    <div style={{ display: "flex", alignItems: "center", background: "#F8F8F8", border: "1.5px solid rgba(204,26,26,0.14)", borderRadius: 8, overflow: "hidden", marginBottom: 6 }}>
      {f.pre && <span style={{ padding: "8px 10px", fontSize: 12, color: "#888", background: "#F0F0F0", borderRight: "1px solid rgba(0,0,0,0.08)" }}>{f.pre}</span>}
      <input value={value} onChange={e => onChange(e.target.value)} placeholder={f.ph}
        style={{ flex: 1, padding: "9px 12px", border: "none", background: "transparent", fontSize: 14, fontWeight: 700, color: "#111", outline: "none", fontFamily: "'DM Mono',monospace" }} />
      {f.suf && <span style={{ padding: "8px 10px", fontSize: 11, color: "#888" }}>{f.suf}</span>}
    </div>
    <div style={{ fontSize: 10, color: "#AAA", lineHeight: 1.4 }}>{f.tip}</div>
    {f.bm && <div style={{ fontSize: 10, fontWeight: 700, color: "#16A34A", marginTop: 3 }}>Benchmark: {f.bm}</div>}
  </div>
);

const PerformanceStep = ({ settings, upd }) => {
  const teams = [...new Set((settings.team?.reps || []).map(r => r.team).filter(Boolean))];
  const allTeams = teams.length > 0 ? teams : ["Global"];
  const [activeTeam, setActiveTeam] = React.useState(allTeams[0]);

  // Storage: settings.targets.byTeam[teamName][field]
  // Falls back to top-level settings.targets for backwards compat
  const globalT = settings.targets || {};
  const byTeam  = globalT.byTeam || {};

  const getVal = (team, k) => byTeam[team]?.[k] ?? globalT[k] ?? "";
  const setVal = (team, k, v) => {
    const updated = {
      ...globalT,
      configured: true,
      byTeam: {
        ...byTeam,
        [team]: { ...(byTeam[team] || {}), [k]: v },
      },
    };
    upd("targets", updated);
  };

  // Also keep top-level fields in sync with the first/only team for backwards compat
  const setValSynced = (team, k, v) => {
    const isFirst = team === allTeams[0];
    const updated = {
      ...globalT,
      ...(isFirst ? { [k]: v } : {}),
      configured: true,
      byTeam: {
        ...byTeam,
        [team]: { ...(byTeam[team] || {}), [k]: v },
      },
    };
    upd("targets", updated);
  };

  return (
    <div>
      <SH title="Performance Targets" sub="Set goals per team — these will be compared against live HubSpot data" />

      {/* Team tabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
        {allTeams.map((team, i) => {
          const active = activeTeam === team;
          const color  = SEG_COLORS[i % SEG_COLORS.length];
          return (
            <button key={team} onClick={() => setActiveTeam(team)}
              style={{ padding: "8px 18px", borderRadius: 20, border: `1.5px solid ${active ? color : "rgba(0,0,0,0.10)"}`,
                background: active ? color : "#FFF", color: active ? "#FFF" : "#555",
                fontSize: 12, fontWeight: 700, cursor: "pointer", transition: "all 0.15s" }}>
              {team}
            </button>
          );
        })}
      </div>

      {/* Fields for active team */}
      {allTeams.map(team => team === activeTeam && (
        <div key={team}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 14, marginBottom: 14 }}>
            {PERF_FIELDS.slice(0, 5).map(f => (
              <PerfFieldCard key={f.k} f={f} value={getVal(team, f.k)} onChange={v => setValSynced(team, f.k, v)} />
            ))}
          </div>

        </div>
      ))}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// STEP 6 — Retention & NRR
// ─────────────────────────────────────────────────────────────────────────────
const RetentionStep = ({ settings, upd }) => {
  const nrrCfg  = settings.nrr    || {};
  const targets = settings.targets || {};
  const segs    = getSegments(settings);

  const setNrr = (k, v) => upd("nrr", { ...(settings.nrr || {}), [k]: v, configured: true });
  const setTgt = (k, v) => upd(`targets.${k}`, v);

  return (
    <div>
      <SH title="NRR & Retention Targets" sub="Drives the Retention & NRR screen and the Renewal row in your Revenue Matrix" />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 16, marginBottom: 28 }}>
        {[
          { l: "NRR Target",        k: "nrr",            obj: "targets", suf: "%", ph: "110",  tip: "Net Revenue Retention. 110%+ is elite." },
          { l: "GRR Target",        k: "grr",            obj: "nrr",     suf: "%", ph: "90",   tip: "Gross Revenue Retention — renewals only, no expansion." },
          { l: "Logo Churn Target", k: "logoChurn",      obj: "nrr",     suf: "%", ph: "5",    tip: "% of customers expected to churn this year." },
          { l: "Starting ARR",      k: "startingArr",    obj: "nrr",     pre: "$", ph: "8M",   tip: "Opening ARR for the year — anchor for NRR math." },
          { l: "Expansion Target",  k: "expansionTarget",obj: "nrr",     pre: "$", ph: "1M",   tip: "Upsell + cross-sell ARR target for the year." },
          { l: "Churn $ Target",    k: "churnTarget",    obj: "nrr",     pre: "$", ph: "500K", tip: "Expected gross dollar churn." },
        ].map(f => {
          const val = f.obj === "targets" ? (targets[f.k] || "") : (nrrCfg[f.k] || "");
          const onChange = f.obj === "targets" ? (v => setTgt(f.k, v)) : (v => setNrr(f.k, v));
          return (
            <div key={f.k} style={{ background: "#FFF", border: "1px solid rgba(0,0,0,0.07)", borderRadius: 10, padding: "16px 18px" }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#555", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 6 }}>{f.l}</div>
              <div style={{ display: "flex", alignItems: "center", background: "#F8F8F8", border: "1.5px solid rgba(204,26,26,0.14)", borderRadius: 8, overflow: "hidden", marginBottom: 8 }}>
                {f.pre && <span style={{ padding: "8px 10px", fontSize: 12, color: "#888", background: "#F0F0F0", borderRight: "1px solid rgba(0,0,0,0.08)" }}>{f.pre}</span>}
                <input value={val} onChange={e => onChange(e.target.value)} placeholder={f.ph}
                  style={{ flex: 1, padding: "9px 12px", border: "none", background: "transparent", fontSize: 14, fontWeight: 700, color: "#111", outline: "none", fontFamily: "'DM Mono',monospace" }} />
                {f.suf && <span style={{ padding: "8px 10px", fontSize: 11, color: "#888" }}>{f.suf}</span>}
              </div>
              <div style={{ fontSize: 10, color: "#AAA", lineHeight: 1.4 }}>{f.tip}</div>
            </div>
          );
        })}
      </div>

      {segs.length > 0 && (
        <>
          <SH title="NRR by Segment" sub="Track starting ARR and retention target per segment" />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))", gap: 12 }}>
            {segs.map((seg, i) => {
              const segNrr   = nrrCfg.segments?.[seg] || {};
              const setSegNrr = (k, v) => upd("nrr.segments", { ...(nrrCfg.segments || {}), [seg]: { ...segNrr, [k]: v } });
              return (
                <div key={seg} style={{ background: "#FFF", border: "1px solid rgba(0,0,0,0.07)", borderRadius: 10, padding: "14px 16px", borderTop: `3px solid ${SEG_COLORS[i % SEG_COLORS.length]}` }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#111", marginBottom: 12 }}>{seg}</div>
                  {[{ k: "startingArr", l: "Starting ARR", pre: "$" }, { k: "nrrTarget", l: "NRR Target", suf: "%" }, { k: "churnBudget", l: "Churn Budget", pre: "$" }].map(f => (
                    <div key={f.k} style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 9, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 3 }}>{f.l}</div>
                      <div style={{ display: "flex", alignItems: "center", background: "#F8F8F8", border: "1px solid rgba(0,0,0,0.10)", borderRadius: 6, overflow: "hidden" }}>
                        {f.pre && <span style={{ padding: "5px 7px", fontSize: 11, color: "#888", background: "#F0F0F0", borderRight: "1px solid rgba(0,0,0,0.08)" }}>{f.pre}</span>}
                        <input value={segNrr[f.k] || ""} onChange={e => setSegNrr(f.k, e.target.value)} placeholder="—"
                          style={{ flex: 1, padding: "6px 8px", border: "none", background: "transparent", fontSize: 12, fontWeight: 600, color: "#111", outline: "none", fontFamily: "'DM Mono',monospace" }} />
                        {f.suf && <span style={{ padding: "5px 7px", fontSize: 11, color: "#888" }}>{f.suf}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// STEP 7 — GTM Assumptions
// ─────────────────────────────────────────────────────────────────────────────
const GTMStep = ({ settings, upd, plan, setPlan }) => {
  const annualTarget = parseDollar(settings?.revenue?.target || "");
  const t = settings.targets || {};
  const repsCount = (settings.team?.reps || []).filter(r => r.name).length;

  const setT = (k, v) => upd("targets", { ...(settings.targets || {}), [k]: v, configured: true });
  const setP = (k, v) => { const next = { ...plan, [k]: v }; setPlan(next); savePlan(next); };

  const wr  = +(t.winRate || 0) / 100;
  const ads = parseDollar(t.avgDealSize || "");
  const cyc = +(t.salesCycle || 0);
  const cov = +(t.coverageRatio || 3);

  const dealsNeeded    = wr > 0 && ads > 0 ? Math.ceil(annualTarget / (ads * wr)) : null;
  const pipelineNeeded = wr > 0 ? Math.ceil(annualTarget / wr) : null;
  const pipelineWithCov = pipelineNeeded ? pipelineNeeded * cov : null;
  const dealsPerRepMo  = dealsNeeded && repsCount > 0 ? (dealsNeeded / repsCount / 12).toFixed(1) : null;
  const pipelinePerRep = pipelineNeeded && repsCount > 0 ? pipelineNeeded / repsCount : null;
  const ramped         = ads > 0 && wr > 0 && cyc > 0 ? Math.round((12 / (cyc / 30)) * ads * wr) : null;
  const repsNeeded     = ramped && annualTarget > 0 ? Math.ceil(annualTarget / ramped) : null;

  return (
    <div>
      <SH title="GTM Assumptions" sub="These values sync from Performance Targets — override here for scenario modeling" />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 16, marginBottom: 24 }}>
        {[
          { k: "winRate",       l: "Win Rate",           suf: "%" , src: "t", ph: "25",    tip: "Saved to Performance Targets" },
          { k: "avgDealSize",   l: "Avg Deal Size",      pre: "$",  src: "t", ph: "25000", tip: "Saved to Performance Targets" },
          { k: "salesCycle",    l: "Sales Cycle",        suf: "d",  src: "t", ph: "60",    tip: "Saved to Performance Targets" },
          { k: "coverageRatio", l: "Pipeline Coverage",  suf: "×",  src: "t", ph: "3",     tip: "Required pipeline ÷ target" },
          { k: "rampDays",      l: "Ramp Time",          suf: "d",  src: "t", ph: "90",    tip: "Saved to Performance Targets" },
          { k: "gtmSdrRatio",   l: "SDR : AE Ratio",     suf: " SDR/AE", src: "p", ph: "1", tip: "Pipeline generation ratio" },
        ].map(f => {
          const val = f.src === "t" ? (t[f.k] || "") : (plan[f.k] || "");
          const onChange = f.src === "t" ? (v => setT(f.k, v)) : (v => setP(f.k, v));
          return (
            <div key={f.k} style={{ background: "#FFF", border: "1px solid rgba(0,0,0,0.07)", borderRadius: 10, padding: "14px 16px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: "#555", textTransform: "uppercase", letterSpacing: "0.07em" }}>{f.l}</span>
                {f.src === "t" && <span style={{ fontSize: 8, background: "rgba(22,163,74,0.12)", color: "#16A34A", padding: "1px 5px", borderRadius: 3, fontWeight: 700 }}>SETTINGS</span>}
              </div>
              <div style={{ display: "flex", alignItems: "center", background: "#F8F8F8", border: "1.5px solid rgba(204,26,26,0.14)", borderRadius: 8, overflow: "hidden" }}>
                {f.pre && <span style={{ padding: "7px 9px", fontSize: 11, color: "#888", background: "#F0F0F0", borderRight: "1px solid rgba(0,0,0,0.08)" }}>{f.pre}</span>}
                <input value={val} onChange={e => onChange(e.target.value)} placeholder={f.ph}
                  style={{ flex: 1, padding: "8px 10px", border: "none", background: "transparent", fontSize: 14, fontWeight: 700, color: "#111", outline: "none", fontFamily: "'DM Mono',monospace" }} />
                {f.suf && <span style={{ padding: "7px 9px", fontSize: 11, color: "#888" }}>{f.suf}</span>}
              </div>
              <div style={{ fontSize: 10, color: "#AAA", marginTop: 5 }}>{f.tip}</div>
            </div>
          );
        })}
      </div>

      <Card style={{ marginBottom: 16 }}>
        <div style={{ padding: "16px 20px" }}>
          <SH title="Derived Plan Math" sub={`Calculated from assumptions above + ${fmtM(annualTarget)} annual target`} />
          {annualTarget > 0 && (wr > 0 || ads > 0) ? (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12 }}>
              {[
                { l: "Pipeline Required",     v: pipelineNeeded  ? fmtM(pipelineNeeded)    : "—", hi: true  },
                { l: "Pipeline w/ Coverage",  v: pipelineWithCov ? fmtM(pipelineWithCov)   : "—", hi: false },
                { l: "Total Deals Needed",    v: dealsNeeded     ? fmtNum(dealsNeeded)      : "—", hi: false },
                { l: "Deals / Rep / Month",   v: dealsPerRepMo   ? dealsPerRepMo            : "—", hi: false },
                { l: "Pipeline / Rep",        v: pipelinePerRep  ? fmtM(pipelinePerRep)    : "—", hi: false },
                { l: "Est. Reps Needed",      v: repsNeeded      ? fmtNum(repsNeeded)       : "—", hi: true  },
              ].map((d, i) => (
                <div key={i} style={{ background: d.hi ? "rgba(204,26,26,0.05)" : "#F8F8F8", border: d.hi ? "1px solid rgba(204,26,26,0.15)" : "1px solid rgba(0,0,0,0.06)", borderRadius: 10, padding: "14px 16px" }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: d.hi ? "#CC1A1A" : "#888", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 5 }}>{d.l}</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: d.hi ? "#CC1A1A" : "#111", fontFamily: "'DM Mono',monospace" }}>{d.v}</div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ padding: "24px 0", textAlign: "center", color: "#AAA", fontSize: 12 }}>Set Win Rate and Avg Deal Size in Performance Targets to see pipeline math.</div>
          )}
        </div>
      </Card>
    </div>
  );
};


// ─────────────────────────────────────────────────────────────────────────────
// ── STEP 0 — HubSpot Connection ───────────────────────────────────────────────
const HubSpotStep = ({ settings, upd }) => (
  <div>
    <SH title="HubSpot Connection" sub="Connect your HubSpot portal to pull live deal data"/>
    <div style={{padding:"12px 16px", background:"rgba(204,26,26,0.06)", border:"1px solid rgba(204,26,26,0.12)", borderRadius:8, fontSize:12, color:"#CC1A1A", marginBottom:16, lineHeight:1.8}}>
      💡 HubSpot → Settings → Integrations → Private Apps → Create app<br/>
      Required scopes: <strong>crm.objects.deals.read · crm.objects.owners.read · crm.objects.companies.read</strong>
    </div>
    <Inp label="Private App Token" value={settings.hubspot?.token||""} placeholder="pat-na1-xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
      hint="Stored only in your browser — never sent to any server except HubSpot directly"
      onChange={v => { upd("hubspot.token", v); upd("hubspot.configured", !!v); }}/>
    <Inp label="HubSpot Portal ID" value={settings.hubspot?.portalId||""} placeholder="e.g. 12345678"
      hint="Found in your HubSpot URL: app.hubspot.com/contacts/XXXXXXXX"
      onChange={v => upd("hubspot.portalId", v)}/>
    {settings.hubspot?.token && (
      <div style={{padding:"10px 14px", background:"rgba(22,163,74,0.08)", border:"1px solid rgba(22,163,74,0.20)", borderRadius:8, fontSize:12, color:"#16A34A", marginTop:8}}>
        ✓ Token saved — click Next to map your pipeline stages
      </div>
    )}
  </div>
);

// ── STEP 1 — Stage Mapping ─────────────────────────────────────────────────────
const StagesStep = ({ settings, upd }) => (
  <div>
    <SH title="Stage Mapping" sub="Tag each HubSpot stage as pipeline, won, lost, or ignore — this drives all your revenue math"/>
    <StageMapper s={settings} upd={upd}/>
  </div>
);

// ── STEP 2 — Field Mapping ─────────────────────────────────────────────────────
const FieldsStep = ({ settings, upd }) => (
  <div>
    <SH title="Field Mapping" sub="Map RevXcel's logical field names to your HubSpot property names — defaults work for most portals"/>
    <FieldMapper s={settings} upd={upd}/>
  </div>
);

// ── STEP 3 — Company Info ──────────────────────────────────────────────────────
const CompanyStep = ({ settings, upd }) => (
  <div>
    <SH title="Company Info" sub="Basic details used across the platform"/>
    <Inp label="Client / Company Name" value={settings.company?.name||""}       onChange={v => upd("company.name", v)}       placeholder="e.g. Acme Corp"/>
    <Inp label="CRM Platform"          value={settings.company?.crm||""}        onChange={v => upd("company.crm", v)}        placeholder="HubSpot"/>
    <Inp label="Fiscal Year"           value={settings.company?.fiscalYear||""} onChange={v => upd("company.fiscalYear", v)} placeholder={String(new Date().getFullYear())} type="number"/>
  </div>
);

const STEPS = [
  { id: "hubspot",   label: "HubSpot",          icon: Link,      check: (s) => !!s?.hubspot?.configured,
    desc: "Connect your HubSpot private app token" },
  { id: "stages",    label: "Stage Mapping",     icon: Database,  check: (s) => !!s?.stages?.configured,
    desc: "Tag pipeline, won, lost stages" },
  { id: "fields",    label: "Field Mapping",     icon: Settings,  check: (s) => !!s?.fieldMap?.configured,
    desc: "Map HubSpot property names" },
  { id: "company",   label: "Company Info",      icon: Building2, check: (s) => !!s?.company?.name,
    desc: "Name, CRM platform, fiscal year" },
  { id: "target",    label: "Revenue Target",   icon: Target,    check: (s) => !!parseDollar(s?.revenue?.target || ""),
    clear: (s) => ({ ...s, revenue: { target: "", monthlyTargets: {}, configured: false } }) },
  { id: "team",      label: "Quota & Team",     icon: Users,     check: (s) => (s?.team?.reps || []).filter(r => r.name && r.quota).length > 0,
    clear: (s) => ({ ...s, team: { reps: [], configured: false } }) },
  { id: "matrix",    label: "Revenue Matrix",   icon: BarChart2, check: (s) => (s?.segments?.list || []).filter(t => t.name).length > 0,
    clear: (s) => { try { localStorage.removeItem("rx_annual_plan_v3"); } catch {} return { ...s, segments: { list: [], configured: false } }; } },
  { id: "perf",      label: "Performance",      icon: TrendingUp,check: (s) => !!(s?.targets?.winRate || s?.targets?.avgDealSize),
    clear: (s) => ({ ...s, targets: { configured: false } }) },
  { id: "retention", label: "Retention & NRR",  icon: RefreshCw, check: (s) => !!(s?.targets?.nrr || s?.nrr?.grr),
    clear: (s) => ({ ...s, nrr: {}, targets: { ...(s.targets || {}), nrr: "", grr: "" } }) },
  { id: "gtm",       label: "GTM Assumptions",  icon: Settings,  check: (s) => !!(s?.targets?.winRate && s?.targets?.coverageRatio),
    clear: (s) => ({ ...s, funnel: {}, targets: { ...(s.targets || {}), coverageRatio: "", bdrCount: "", aeCount: "" } }) },
];


export default function AnnualPlan({ settings, onSave, hsData, onLoadData, userId }) {
  const [activeStep, setActiveStep] = useState("target");
  const [plan, setPlan] = useState(() => loadPlan());
  const [saved, setSaved] = useState(false);

  // Deep updater — writes a dot-path into settings and persists globally
  const upd = useCallback((path, value) => {
    const next = deepSet(settings, path, value);
    onSave?.(next);
    setSaved(true);
    setTimeout(() => setSaved(false), 1800);
  }, [settings, onSave]);

  const annualTarget = parseDollar(settings?.revenue?.target || "");
  const segs         = getSegments(settings);
  const displaySegs  = segs.length ? segs : ["Total"];
  const matrixTotal  = grandTotal(plan, displaySegs);
  const totalQuota   = (settings?.team?.reps || []).reduce((s, r) => s + parseDollar(r.quota || ""), 0);
  const completedCount = STEPS.filter(st => st.check(settings, plan)).length;

  const activeIdx = STEPS.findIndex(s => s.id === activeStep);
  const goNext = () => { if (activeIdx < STEPS.length - 1) setActiveStep(STEPS[activeIdx + 1].id); };
  const goPrev = () => { if (activeIdx > 0) setActiveStep(STEPS[activeIdx - 1].id); };
  const clearStep = () => {
    const step = STEPS[activeIdx];
    if (!step?.clear) return;
    if (!window.confirm(`Clear all data for "${step.label}"? This cannot be undone.`)) return;
    const cleared = step.clear(settings);
    onSave(cleared);
  };

  return (
    <div className="fade-up" style={{ maxWidth: 1200, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ background: "linear-gradient(135deg,#CC1A1A,#991B1B)", borderRadius: 14, padding: "20px 26px", marginBottom: 20, color: "#FFF", display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 800, marginBottom: 4 }}>📋 Annual Plan Builder</div>
          <div style={{ fontSize: 12, opacity: 0.85 }}>Set your plan parameters — all fields save back to global settings automatically</div>
        </div>
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap", alignItems: "flex-start" }}>
          {[
            { l: "Revenue Target", v: annualTarget ? fmtM(annualTarget) : "—" },
            { l: "Matrix Total",   v: matrixTotal  ? fmtM(matrixTotal)  : "—" },
            { l: "Quota Pool",     v: totalQuota   ? fmtM(totalQuota)   : "—" },
            { l: "Setup",         v: `${completedCount}/${STEPS.length}` },
          ].map((s, i) => (
            <div key={i} style={{ textAlign: "right" }}>
              <div style={{ fontSize: 9, opacity: 0.7, textTransform: "uppercase", letterSpacing: "0.07em" }}>{s.l}</div>
              <div style={{ fontSize: 16, fontWeight: 800, fontFamily: "'DM Mono',monospace" }}>{s.v}</div>
            </div>
          ))}
          {saved && <div style={{ fontSize: 11, background: "rgba(22,163,74,0.30)", padding: "6px 12px", borderRadius: 6, fontWeight: 700, alignSelf: "center" }}>✓ Saved</div>}
        </div>
      </div>

      {/* Body */}
      <div style={{ display: "grid", gridTemplateColumns: "216px 1fr", gap: 18, alignItems: "start" }}>
        {/* Sidebar */}
        <div style={{ background: "#FFF", border: "1px solid rgba(0,0,0,0.07)", borderRadius: 12, padding: "12px 10px", position: "sticky", top: 18 }}>
          {STEPS.map((step) => {
            const active = activeStep === step.id;
            const done   = step.check(settings, plan);
            const Icon   = step.icon;
            return (
              <button key={step.id} onClick={() => setActiveStep(step.id)} style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "10px 12px", borderRadius: 8, border: "none", cursor: "pointer", marginBottom: 2, textAlign: "left", background: active ? "rgba(204,26,26,0.08)" : "transparent", borderLeft: `2px solid ${active ? "#CC1A1A" : "transparent"}`, transition: "all 0.12s" }}>
                <div style={{ width: 28, height: 28, borderRadius: 7, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", background: done ? "rgba(22,163,74,0.12)" : active ? "rgba(204,26,26,0.10)" : "#F0F0F0" }}>
                  {done ? <CheckCircle size={14} style={{ color: "#16A34A" }} /> : <Icon size={14} style={{ color: active ? "#CC1A1A" : "#888" }} />}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11, fontWeight: active ? 700 : 500, color: active ? "#CC1A1A" : "#333", lineHeight: 1.2 }}>{step.label}</div>
                  {step.id !== "history" && <div style={{ fontSize: 9, color: done ? "#16A34A" : "#CCC", marginTop: 1 }}>{done ? "✓ configured" : "not set"}</div>}
                </div>
              </button>
            );
          })}
          <div style={{ marginTop: 12, padding: "10px 12px", background: "#F8F8F8", borderRadius: 8 }}>
            <div style={{ fontSize: 10, color: "#888", marginBottom: 4 }}>Progress</div>
            <div style={{ height: 4, background: "#E8E8E8", borderRadius: 99, overflow: "hidden" }}>
              <div style={{ width: `${Math.round(completedCount / STEPS.length * 100)}%`, height: "100%", background: "#16A34A", borderRadius: 99 }} />
            </div>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#16A34A", marginTop: 4 }}>{completedCount}/{STEPS.length} sections complete</div>
          </div>
        </div>

        {/* Content */}
        <div>
          <div style={{ background: "#FFF", border: "1px solid rgba(0,0,0,0.07)", borderRadius: 12, padding: "24px 28px", marginBottom: 14 }}>
            {activeStep === "hubspot"   && <HubSpotStep  settings={settings} upd={upd} />}
            {activeStep === "stages"    && <StagesStep   settings={settings} upd={upd} />}
            {activeStep === "fields"    && <FieldsStep   settings={settings} upd={upd} />}
            {activeStep === "company"   && <CompanyStep  settings={settings} upd={upd} />}
            {activeStep === "target"    && <RevenueTargetStep  settings={settings} upd={upd} />}
            {activeStep === "matrix"    && <RevenueMatrixStep  settings={settings} upd={upd} plan={plan} setPlan={setPlan} />}
            {activeStep === "team"      && <QuotaTeamStep      settings={settings} upd={upd} hsData={hsData} />}
            {activeStep === "perf"      && <PerformanceStep    settings={settings} upd={upd} />}
            {activeStep === "retention" && <RetentionStep      settings={settings} upd={upd} />}
            {activeStep === "gtm"       && <GTMStep            settings={settings} upd={upd} plan={plan} setPlan={setPlan} />}

          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <button onClick={goPrev} disabled={activeIdx === 0}
              style={{ padding: "10px 22px", borderRadius: 8, border: "1px solid rgba(0,0,0,0.10)", background: "transparent", color: activeIdx === 0 ? "#CCC" : "#555", fontSize: 13, fontWeight: 600, cursor: activeIdx === 0 ? "not-allowed" : "pointer" }}>
              ← Previous
            </button>
            <div style={{ display: "flex", gap: 10 }}>
              {STEPS[activeIdx]?.clear && (
                <button onClick={clearStep}
                  style={{ padding: "10px 18px", borderRadius: 8, border: "1px solid rgba(204,26,26,0.25)", background: "transparent", color: "#CC1A1A", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                  🗑 Clear this step
                </button>
              )}
              {activeIdx < STEPS.length - 1 && (
                <button onClick={goNext}
                  style={{ padding: "10px 22px", borderRadius: 8, border: "none", background: "linear-gradient(135deg,#CC1A1A,#991B1B)", color: "#FFF", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                  Next: {STEPS[activeIdx + 1].label} →
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
